import InputField from '../../inputs/inputField'
import RadioInput from '../../inputs/radioInput'
import CalendarInput from '../../inputs/calendarInput'
import TypographyParagraph from '../../typography/TypographyParagraph'
import Toggle from '../../inputs/toggle'
import { useEffect } from 'react'

export default function ProofOfExhibitionForm({
	formData,
	setFormData,
	requiredFields,
	errorFields,
	address,
}: {
	formData: any
	setFormData: (data: any) => void
	requiredFields: any[]
	errorFields: any[]
	address?: string
}) {
	useEffect(() => {
		// Default to countersign required and artist initiated
		if (Object.keys(formData).length == 0) {
			setFormData({
				artistInitiated: true,
				countersignRequired: true,
				...(address ? { ownerAddress: address } : {}),
			})
		}
	}, [formData])

	const handleDataChange = (name: string, value: any) => {
		let newData = { ...formData }
		newData[name] = value
		// If switching the artistInitiated flag, also switch the wallet fields contents
		if (
			name == 'artistInitiated' &&
			formData.artistInitiated !== newData.artistInitiated
		) {
			newData.ownerAddress = formData.receiverAddress
			newData.receiverAddress = formData.ownerAddress
		}
		setFormData(newData)
	}

	const isArtistInitiated = () => {
		return formData.artistInitiated
	}

	const isCountersignRequired = () => {
		return formData.countersignRequired
	}

	return (
		<div className="flex w-full flex-col gap-y-2">
			<div className="mb-2 flex flex-row items-center">
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					I'm the{' '}
				</TypographyParagraph>
				<div className="flex flex-row items-center">
					<RadioInput
						key="Owner/Artist"
						name="Owner/Artist"
						size="large"
						onClick={() => handleDataChange('artistInitiated', true)}
						current={formData.artistInitiated == true}
					/>
					<RadioInput
						key="Receiver/Exhibitor"
						name="Receiver/Exhibitor"
						size="large"
						onClick={() => handleDataChange('artistInitiated', false)}
						current={formData.artistInitiated == false}
					/>
				</div>
			</div>
			<div className="flex flex-row gap-x-4">
				<div className="w-1/2">
					<CalendarInput
						name="start date"
						containerWidth="w-full"
						value={formData.startDate}
						maxDate={formData.endDate}
						displayRequired={requiredFields.find(
							field => field.slug == 'startDate',
						)}
						onChange={e => {
							handleDataChange('startDate', e.target.value)
						}}
						isError={errorFields.find(
							requiredField => requiredField.slug == 'startDate',
						)}
					/>
				</div>
				<div className="w-1/2">
					<CalendarInput
						name="end date"
						containerWidth="w-full"
						value={formData.endDate}
						minDate={formData.startDate}
						displayRequired={requiredFields.find(
							field => field.slug == 'endDate',
						)}
						onChange={e => {
							handleDataChange('endDate', e.target.value)
						}}
						isError={errorFields.find(
							requiredField => requiredField.slug == 'endDate',
						)}
					/>
				</div>
			</div>
			<InputField
				name="exhibition name"
				type="text"
				placeholder='Ex. "Creating Connections"'
				containerWidth="w-full"
				value={formData.name}
				displayRequired={requiredFields.find(field => field.slug == 'name')}
				onChange={e => {
					handleDataChange('name', e.target.value)
				}}
				isError={errorFields.find(
					requiredField => requiredField.slug == 'name',
				)}
			/>
			<InputField
				name="exhibition location"
				type="text"
				placeholder='Ex. "New York, US"'
				containerWidth="w-full"
				value={formData.location}
				displayRequired={requiredFields.find(field => field.slug == 'location')}
				onChange={e => {
					handleDataChange('location', e.target.value)
				}}
				isError={errorFields.find(
					requiredField => requiredField.slug == 'location',
				)}
			/>
			<InputField
				name="owner wallet address"
				type="text"
				placeholder="Enter owner wallet address"
				containerWidth="w-full"
				value={formData.ownerAddress}
				displayRequired={
					requiredFields.find(field => field.slug == 'ownerAddress') &&
					(isCountersignRequired() || isArtistInitiated())
				}
				onChange={e => {
					handleDataChange('ownerAddress', e.target.value)
				}}
				isError={
					errorFields.find(
						requiredField => requiredField.slug == 'ownerAddress',
					) &&
					(isCountersignRequired() || isArtistInitiated())
				}
			/>
			<InputField
				name="exhibitor wallet address"
				type="text"
				placeholder="Enter exhibitor wallet address"
				containerWidth="w-full"
				value={formData.receiverAddress}
				displayRequired={
					requiredFields.find(field => field.slug == 'receiverAddress') &&
					(isCountersignRequired() || !isArtistInitiated())
				}
				onChange={e => {
					handleDataChange('receiverAddress', e.target.value)
				}}
				isError={
					errorFields.find(
						requiredField => requiredField.slug == 'receiverAddress',
					) &&
					(isCountersignRequired() || !isArtistInitiated())
				}
			/>
			<InputField
				name="external link"
				type="text"
				placeholder="https://"
				containerWidth="w-full"
				value={formData.url}
				displayRequired={requiredFields.find(field => field.slug == 'url')}
				onChange={e => {
					handleDataChange('url', e.target.value)
				}}
				isError={errorFields.find(requiredField => requiredField.slug == 'url')}
			/>
			<InputField
				name="description"
				lines={4}
				placeholder="Add context to this offchain history signature"
				containerWidth="w-full"
				additionalClasses="h-32"
				value={formData.description}
				displayRequired={requiredFields.find(
					field => field.slug == 'description',
				)}
				onChange={e => {
					handleDataChange('description', e.target.value)
				}}
				isError={errorFields.find(
					requiredField => requiredField.slug == 'description',
				)}
			/>
			<div className="mt-2 flex flex-row">
				<Toggle
					name={''}
					isEnabled={formData.countersignRequired}
					onEnabledChange={newEnabled =>
						handleDataChange('countersignRequired', newEnabled)
					}
				></Toggle>
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					<b>Require Countersign: </b>Choose to require a countersignature for
					this attestation. If selected, the signature awaits countersigning;
					otherwise, it's complete upon submission.
				</TypographyParagraph>
			</div>
		</div>
	)
}
